import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, addDoc } from "firebase/firestore";
import { FiArrowLeft, FiSave, FiPlus, FiTrash2 } from "react-icons/fi";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

// ─── Design constants ───────────────────────────────────────────────
const INPUT_STYLE = {
    fontFamily: "'Quicksand', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    padding: "10px 14px",
    borderRadius: 12,
    border: "1.5px solid #E0E0E0",
    background: "#f7f9fb",
    width: "100%",
    outline: "none",
    color: "#000",
    boxSizing: "border-box",
    transition: "border-color 0.18s",
};

const LABEL_STYLE = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "#606060",
    display: "block",
    marginBottom: 5,
};

const CARD_STYLE = {
    background: "#fff",
    borderRadius: 20,
    border: "1px solid #E0E0E0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "22px 24px",
    marginBottom: 20,
};

function Field({ label, children }) {
    return (
        <div>
            <label style={LABEL_STYLE}>{label}</label>
            {children}
        </div>
    );
}

// ─── Spinner ──────────────────────────────────────────────────────────
function Spinner() {
    return (
        <>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        </>
    );
}

function AddFlashcards() {
    const navigate = useNavigate();
    const db = getFirestore();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [words, setWords] = useState([]);
    const [saving, setSaving] = useState(false);
    const [leaveTo, setLeaveTo] = useState(null);
    const [error, setError] = useState("");

    // Confirm-on-exit guard (in-app navigation + tab close).
    useUnsavedChanges(!saving && leaveTo === null);

    useEffect(() => {
        if (leaveTo !== null) navigate(leaveTo);
    }, [leaveTo, navigate]);

    const handleWordChange = (idx, key, value) => {
        setWords(words => words.map((w, i) => (i === idx ? { ...w, [key]: value } : w)));
    };

    const handleAddWord = () => {
        setWords(words => [...words, { word: "", translation: "" }]);
    };

    const handleRemoveWord = (idx) => {
        setWords(words => words.filter((_, i) => i !== idx));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            setError("Title is required.");
            return;
        }
        setSaving(true);
        await addDoc(collection(db, "flashcards"), { title, description, words });
        setSaving(false);
        setLeaveTo(-1);
    };

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 720, margin: "0 auto" }}>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

            {/* Page header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="primary-button"
                        style={{ backgroundColor: "#f0f0f0", color: "#333", borderColor: "#d0d0d0", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, padding: "9px 16px" }}
                    >
                        <FiArrowLeft size={15} /> Back
                    </button>
                    <h1 style={{ margin: 0, fontSize: "1.6rem", color: "#2a9d8f", fontWeight: 700 }}>Add Flashcard Set</h1>
                </div>
                <button
                    type="submit"
                    form="flashcard-form"
                    className="primary-button"
                    disabled={saving}
                    style={{ backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80", display: "inline-flex", alignItems: "center", gap: 7, fontSize: 14, padding: "9px 20px" }}
                >
                    {saving ? <Spinner /> : <FiSave size={14} />}
                    {saving ? "Saving…" : "Save Set"}
                </button>
            </div>

            <form id="flashcard-form" onSubmit={handleSave}>
                {/* Error banner */}
                {error && (
                    <div style={{ background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 12, padding: "12px 16px", color: "#c0392b", fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
                        {error}
                    </div>
                )}

                {/* Set Details card */}
                <div style={CARD_STYLE}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f", marginBottom: 18 }}>Set Details</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <Field label="Title">
                            <input
                                type="text"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                style={{ ...INPUT_STYLE }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                required
                                placeholder="Flashcard set title"
                            />
                        </Field>
                        <Field label="Description (optional)">
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                rows={2}
                                style={{ ...INPUT_STYLE, resize: "vertical" }}
                                onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                placeholder="Brief description of this flashcard set"
                            />
                        </Field>
                    </div>
                </div>

                {/* Words card */}
                <div style={CARD_STYLE}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#2a9d8f" }}>Words</span>
                            <span style={{ background: "#2a9d8f", color: "#fff", fontWeight: 700, fontSize: 11, borderRadius: 999, padding: "2px 9px" }}>{words.length}</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleAddWord}
                            className="primary-button"
                            disabled={saving}
                            style={{ backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 14px" }}
                        >
                            <FiPlus size={14} /> Add Word
                        </button>
                    </div>

                    {words.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "48px 0", color: "#aaa", fontSize: 14, fontWeight: 600 }}>
                            No words yet. Click + Add Word to begin.
                        </div>
                    ) : (
                        <>
                            {words.map((w, idx) => (
                                <div key={idx}>
                                    {idx > 0 && <div style={{ height: 1, background: "#F0F0F0", margin: "10px 0" }} />}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                        {/* Row number badge */}
                                        <div style={{ width: 26, height: 26, borderRadius: "50%", background: "#2a9d8f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                                            {idx + 1}
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Word (English)"
                                            value={w.word}
                                            onChange={e => handleWordChange(idx, "word", e.target.value)}
                                            style={{ ...INPUT_STYLE, flex: "1 1 140px", minWidth: 0 }}
                                            onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                            onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                            required
                                        />
                                        <input
                                            type="text"
                                            placeholder="Translation (Amharic)"
                                            value={w.translation}
                                            onChange={e => handleWordChange(idx, "translation", e.target.value)}
                                            style={{ ...INPUT_STYLE, flex: "1 1 140px", minWidth: 0 }}
                                            onFocus={e => e.target.style.borderColor = "#2a9d8f"}
                                            onBlur={e => e.target.style.borderColor = "#E0E0E0"}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveWord(idx)}
                                            title="Remove word"
                                            style={{ background: "none", border: "none", color: "#e76f51", cursor: "pointer", padding: 6, display: "flex", alignItems: "center", flexShrink: 0 }}
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {/* Dashed add word button below list */}
                            <button
                                type="button"
                                onClick={handleAddWord}
                                style={{ width: "100%", padding: "10px 0", marginTop: 14, border: "1.5px dashed #2a9d8f", borderRadius: 12, background: "none", color: "#2a9d8f", fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                            >
                                <FiPlus size={14} /> Add Word
                            </button>
                        </>
                    )}
                </div>
            </form>
        </div>
    );
}

export default AddFlashcards;
